
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import hrService from '../../services/hrService';

const getToken = (state) => state.auth.token;

export const fetchEmployees = createAsyncThunk('hr/fetchEmployees', async (_, { getState }) => {
  return await hrService.getEmployees(getToken(getState()));
});
export const fetchAttendance = createAsyncThunk('hr/fetchAttendance', async (_, { getState }) => {
  return await hrService.getAttendance(getToken(getState()));
});
export const fetchLeaveRequests = createAsyncThunk('hr/fetchLeaveRequests', async (_, { getState }) => {
  return await hrService.getLeaveRequests(getToken(getState()));
});
export const fetchPayroll = createAsyncThunk('hr/fetchPayroll', async (_, { getState }) => {
  return await hrService.getPayroll(getToken(getState()));
});
export const fetchRecruitment = createAsyncThunk('hr/fetchRecruitment', async (_, { getState }) => {
  return await hrService.getRecruitment(getToken(getState()));
});
export const fetchPerformance = createAsyncThunk('hr/fetchPerformance', async (_, { getState }) => {
  return await hrService.getPerformance(getToken(getState()));
});
export const fetchTraining = createAsyncThunk('hr/fetchTraining', async (_, { getState }) => {
  return await hrService.getTraining(getToken(getState()));
});
export const fetchDisciplinary = createAsyncThunk('hr/fetchDisciplinary', async (_, { getState }) => {
  return await hrService.getDisciplinary(getToken(getState()));
});
export const fetchHRAssets = createAsyncThunk('hr/fetchHRAssets', async (_, { getState }) => {
  return await hrService.getHRAssets(getToken(getState()));
});
export const fetchExits = createAsyncThunk('hr/fetchExits', async (_, { getState }) => {
  return await hrService.getExits(getToken(getState()));
});
export const createEmployee = createAsyncThunk('hr/createEmployee', async (data, { getState }) => {
  return await hrService.createEmployee(data, getToken(getState()));
});
export const updateEmployee = createAsyncThunk('hr/updateEmployee', async ({ id, data }, { getState }) => {
  return await hrService.updateEmployee(id, data, getToken(getState()));
});
export const createLeaveRequest = createAsyncThunk('hr/createLeaveRequest', async (data, { getState }) => {
  return await hrService.createLeaveRequest(data, getToken(getState()));
});
export const updateLeaveRequest = createAsyncThunk('hr/updateLeaveRequest', async ({ id, data }, { getState }) => {
  return await hrService.updateLeaveRequest(id, data, getToken(getState()));
});
export const approveLeave = createAsyncThunk('hr/approveLeave', async (leaveRecord, { getState }) => {
  return await hrService.updateLeaveRequest(leaveRecord.id, { ...leaveRecord, status: 'Approved' }, getToken(getState()));
});
export const rejectLeave = createAsyncThunk('hr/rejectLeave', async (leaveRecord, { getState }) => {
  return await hrService.updateLeaveRequest(leaveRecord.id, { ...leaveRecord, status: 'Rejected' }, getToken(getState()));
});
export const createRecruitment = createAsyncThunk('hr/createRecruitment', async (data, { getState }) => {
  return await hrService.createRecruitment(data, getToken(getState()));
});
export const createTraining = createAsyncThunk('hr/createTraining', async (data, { getState }) => {
  return await hrService.createTraining(data, getToken(getState()));
});
export const createDisciplinary = createAsyncThunk('hr/createDisciplinary', async (data, { getState }) => {
  return await hrService.createDisciplinary(data, getToken(getState()));
});
export const createHRAsset = createAsyncThunk('hr/createHRAsset', async (data, { getState }) => {
  return await hrService.createHRAsset(data, getToken(getState()));
});
export const createExit = createAsyncThunk('hr/createExit', async (data, { getState }) => {
  return await hrService.createExit(data, getToken(getState()));
});


export const fetchLeave = fetchLeaveRequests;

const setPending = (state) => { state.loading = true; state.error = null; };
const setError = (state, action) => { state.loading = false; state.error = action.error.message; };

const hrSlice = createSlice({
  name: 'hr',
  initialState: {
    employees: [], attendance: [], leaveRequests: [], payroll: [],
    recruitment: [], performance: [], training: [], disciplinary: [],
    hrAssets: [], exits: [], selectedEmployee: null,
    loading: false, error: null,
  },
  reducers: {
    setSelectedEmployee: (state, action) => { state.selectedEmployee = action.payload; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployees.pending, setPending)
      .addCase(fetchEmployees.fulfilled, (state, action) => { state.loading = false; state.employees = action.payload; })
      .addCase(fetchEmployees.rejected, setError)
      .addCase(fetchAttendance.pending, setPending)
      .addCase(fetchAttendance.fulfilled, (state, action) => { state.loading = false; state.attendance = action.payload; })
      .addCase(fetchAttendance.rejected, setError)
      .addCase(fetchLeaveRequests.pending, setPending)
      .addCase(fetchLeaveRequests.fulfilled, (state, action) => { state.loading = false; state.leaveRequests = action.payload; })
      .addCase(fetchLeaveRequests.rejected, setError)
      .addCase(fetchPayroll.pending, setPending)
      .addCase(fetchPayroll.fulfilled, (state, action) => { state.loading = false; state.payroll = action.payload; })
      .addCase(fetchPayroll.rejected, setError)
      .addCase(fetchRecruitment.pending, setPending)
      .addCase(fetchRecruitment.fulfilled, (state, action) => { state.loading = false; state.recruitment = action.payload; })
      .addCase(fetchRecruitment.rejected, setError)
      .addCase(fetchPerformance.pending, setPending)
      .addCase(fetchPerformance.fulfilled, (state, action) => { state.loading = false; state.performance = action.payload; })
      .addCase(fetchPerformance.rejected, setError)
      .addCase(fetchTraining.pending, setPending)
      .addCase(fetchTraining.fulfilled, (state, action) => { state.loading = false; state.training = action.payload; })
      .addCase(fetchTraining.rejected, setError)
      .addCase(fetchDisciplinary.pending, setPending)
      .addCase(fetchDisciplinary.fulfilled, (state, action) => { state.loading = false; state.disciplinary = action.payload; })
      .addCase(fetchDisciplinary.rejected, setError)
      .addCase(fetchHRAssets.pending, setPending)
      .addCase(fetchHRAssets.fulfilled, (state, action) => { state.loading = false; state.hrAssets = action.payload; })
      .addCase(fetchHRAssets.rejected, setError)
      .addCase(fetchExits.pending, setPending)
      .addCase(fetchExits.fulfilled, (state, action) => { state.loading = false; state.exits = action.payload; })
      .addCase(fetchExits.rejected, setError)
      .addCase(createEmployee.fulfilled, (state, action) => { state.employees.unshift(action.payload); })
      .addCase(updateEmployee.fulfilled, (state, action) => {
        const idx = state.employees.findIndex(e => e.id === action.payload.id);
        if (idx >= 0) state.employees[idx] = action.payload;
      })
      .addCase(createLeaveRequest.fulfilled, (state, action) => { state.leaveRequests.unshift(action.payload); })
      .addCase(updateLeaveRequest.fulfilled, (state, action) => {
        const idx = state.leaveRequests.findIndex(l => l.id === action.payload.id);
        if (idx >= 0) state.leaveRequests[idx] = action.payload;
      })
      .addCase(approveLeave.fulfilled, (state, action) => {
        const idx = state.leaveRequests.findIndex(l => l.id === action.payload.id);
        if (idx >= 0) state.leaveRequests[idx] = action.payload;
      })
      .addCase(rejectLeave.fulfilled, (state, action) => {
        const idx = state.leaveRequests.findIndex(l => l.id === action.payload.id);
        if (idx >= 0) state.leaveRequests[idx] = action.payload;
      })
      .addCase(createRecruitment.fulfilled, (state, action) => { state.recruitment.unshift(action.payload); })
      .addCase(createTraining.fulfilled, (state, action) => { state.training.unshift(action.payload); })
      .addCase(createDisciplinary.fulfilled, (state, action) => { state.disciplinary.unshift(action.payload); })
      .addCase(createHRAsset.fulfilled, (state, action) => { state.hrAssets.unshift(action.payload); })
      .addCase(createExit.fulfilled, (state, action) => { state.exits.unshift(action.payload); });
  },
});

export const { setSelectedEmployee } = hrSlice.actions;
export default hrSlice.reducer;
